import { StdFee } from '@cosmjs/amino'
import { SignerData, DeliverTxResponse } from '@cosmjs/stargate'
import { EncodeObject } from '@cosmjs/proto-signing'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { EVMOS_CHAIN_INFO, INJ_CHAIN_INFO, ISLM_CHAIN_INFO } from '@/config'
import { IbcReturnType } from './signer-types'
import { broadcastEvmosIbcTransaction2, signEvmosIbcTransaction2 } from './signer-evmos'
import { broadcastInjectiveIbcTransaction2, signInjectiveIbcTransaction2 } from './signer-injective'
import { Keplr } from '@keplr-wallet/types'
import { broadcastTx, simulate } from '../utils'
import { broadcastHaqqIbcTransaction2, signHaqqIbcTransaction2, simulateHaqqIbcTransaction2 } from './signer-haqq'
import { IbcSignerParams } from './types'

// @TODO: I don't know why this is here, but consider moving to `src/types/wallet-extensions.d.ts`
declare global {
  interface Window {
    wallet: Keplr
    keplr: Keplr
    leap: Keplr
    cosmostation: {
      providers: {
        keplr: Keplr
      }
    }
  }
}

const simulateIbcTransaction2 = async (
  payload: IbcSignerParams,
  address: string,
  messages: EncodeObject[],
  memo: string
): Promise<number> => {
  // It seems that `simulateEvmosIbcTransaction` is a direct replicate (at least from my
  // perspective) of `SigningStargateClient#simulate` function. It seems things are working
  // without this, but we'll keep this here for context. In case we need it soon, we can
  // always bring it back again. If we never do, let's consider removing this in the future.
  //
  // The fix was simply increasing the multiplier from `calculateFee`
  // if (account.currency.coinDenom === EVMOS_CHAIN_INFO.stakeCurrency.coinDenom) {
  //   return simulateEvmosIbcTransaction(account, address, messages, memo)
  // }

  // It seems that `simulateInjectiveIbcTransaction` is a direct replicate (at least from my
  // perspective) of `SigningStargateClient#simulate` function. It seems things are working
  // without this, but we'll keep this here for context. In case we need it soon, we can
  // always bring it back again. If we never do, let's consider removing this in the future.
  //
  // The fix was simply increasing the multiplier from `calculateFee`
  // if (account.currency.coinDenom === INJ_CHAIN_INFO.stakeCurrency.coinDenom) {
  //   return simulateInjectiveIbcTransaction(account, address, messages, memo)
  // }

  if (payload.denom === ISLM_CHAIN_INFO.stakeCurrency.coinDenom) {
    return simulateHaqqIbcTransaction2(payload, address, messages, memo)
  }

  return simulate(payload.client, address, messages, memo)
}

// These are the recommended functions to sign and broadcast
// ibc transactions that could come from Evmos or Injective to Stride.

// Almost 1:1 API parity with `SigningStargateClient.sign`
//
// While this seemingly sets up Evmos + Ledger, it does not work.
// Evmos does not support Ledger yet (at least until the v12 upgrade)
// The generated endpoint function only generates endpoint to mainnet.
const signIbcTransaction2 = async (
  payload: IbcSignerParams,
  address: string,
  messages: EncodeObject[],
  fee: StdFee,
  memo: string,
  explicitSignerData?: SignerData
): Promise<IbcReturnType> => {
  if (payload.denom === EVMOS_CHAIN_INFO.stakeCurrency.coinDenom) {
    return signEvmosIbcTransaction2(payload, address, messages, fee, memo, explicitSignerData)
  }

  if (payload.denom === ISLM_CHAIN_INFO.stakeCurrency.coinDenom) {
    return signHaqqIbcTransaction2(payload, address, messages, fee, memo, explicitSignerData)
  }

  if (payload.denom === INJ_CHAIN_INFO.stakeCurrency.coinDenom) {
    return signInjectiveIbcTransaction2(payload, address, messages, fee, memo, explicitSignerData)
  }

  const raw = await payload.client.sign(address, messages, fee, memo, explicitSignerData)
  return { type: 'default', value: raw }
}

// v2 functions mirror the original signatures but accept IbcSignerParams where applicable

// Almost 1:1 API parity with StargateClient.broadcastTx
const broadcastIbcTransaction2 = async (
  payload: IbcSignerParams,
  signed: IbcReturnType
): Promise<DeliverTxResponse> => {
  if (signed.type === 'evmos' || signed.type === 'evmos-ledger') {
    return broadcastEvmosIbcTransaction2(payload, signed)
  }

  if (signed.type === 'haqq' || signed.type === 'haqq-ledger') {
    return broadcastHaqqIbcTransaction2(payload, signed)
  }

  if (signed.type === 'injective' || signed.type === 'injective-ledger') {
    return broadcastInjectiveIbcTransaction2(payload, signed)
  }

  const bytes = TxRaw.encode(signed.value).finish()
  return broadcastTx(payload.client, bytes)
}

export { simulateIbcTransaction2, signIbcTransaction2, broadcastIbcTransaction2 }
